/**
 * Bonus (stamp card) — data access (schema §33, docs/features/bonus.md).
 *
 * Every read and write is a security-definer RPC; the browser never touches
 * the bonus tables. The status strings below are the closed sets the SQL
 * returns (§33c-d comments); the server is the only judge of every rule.
 *
 * Fail-soft before the §33 paste: a missing RPC throws `BonusUnavailable`,
 * which the UI treats as "hide Bonus" (Dine In falls back to record_visit).
 */
import { getAnonId } from "@/lib/anonId";
import type { LatLng } from "@/lib/geo";
import { isMissingFunction } from "@/lib/rpc";
import { supabase } from "@/lib/supabase";

export class BonusUnavailable extends Error {
  constructor() {
    super("bonus_unavailable");
  }
}

/** A venue's bonus item (`bonus_item()` in §33c). */
export interface BonusItem {
  name: string;
  name_ro: string | null;
  value_ron: number;
  min_order_ron: number | null;
}

/** The item as snapshotted on a redemption (plus the venue's name). */
export interface BonusItemSnapshot extends BonusItem {
  venue_name?: string;
}

export interface BonusHeld {
  id: string;
  earned_at: string;
  expires_at: string;
}

export type StampStatus =
  | "earned"
  | "minted"
  | "not_participating"
  | "low_accuracy"
  | "too_new"
  | "frozen"
  | "already"
  | "daily_limit"
  | "capped"
  | "too_fast"
  | "staff";

export interface EarnStampResult {
  visit_id: number;
  stamp: StampStatus;
  /** Absent for `staff`. */
  progress?: number;
  required?: number;
  bonus?: BonusHeld | null;
}

export interface BonusRulesPublic {
  stamps_required: number;
  max_counted_per_day: number;
  bonus_ttl_days: number;
  stamp_ttl_days: number;
  max_item_value_ron: number;
  redemption_ttl_seconds: number;
  regulation_version: string;
}

export interface PendingRedemption {
  id: string;
  venue_id: string;
  item: BonusItemSnapshot;
  expires_at: string;
}

export interface MyBonus {
  rules: BonusRulesPublic;
  progress: number;
  stamped_today: boolean;
  bonus: BonusHeld | null;
  pending: PendingRedemption | null;
  last_used: { at: string; venue_id: string; item: BonusItemSnapshot } | null;
}

export interface BonusVenue {
  venue_id: string;
  name: string;
  city: string | null;
  /** false = opted out but still honouring earlier bonuses until `honours_until`. */
  enabled: boolean;
  honours_until: string | null;
  item: BonusItem;
  next_item: BonusItem | null;
  next_effective_at: string | null;
}

export type StartRedemptionResult =
  | {
      status: "pending";
      id: string;
      venue_id: string;
      item: BonusItemSnapshot;
      expires_at: string;
      ttl_seconds: number;
    }
  | {
      status:
        | "no_bonus"
        | "not_participating"
        | "location_required"
        | "too_far"
        | "low_accuracy"
        | "venue_limit"
        | "already_used";
    };

export type ConfirmRedemptionResult =
  | { status: "used"; at: string; item: BonusItemSnapshot }
  | { status: "bad_pin"; tries_left: number }
  | { status: "locked"; until: string }
  | { status: "already_used"; at: string }
  | { status: "expired" | "pin_unset" | "not_found" };

/** The config as a venue sees it (`bonus_venue_view()`, never the PIN hash). */
export interface VenueBonusConfig {
  venue_id: string;
  enabled: boolean;
  disabled_at: string | null;
  item: BonusItem | null;
  next_item: BonusItem | null;
  next_effective_at: string | null;
  non_alcoholic_confirmed: boolean;
  pin_set: boolean;
  pin_set_at: string | null;
  max_item_value_ron: number;
  stamps_30d: number;
  redemptions_30d: number;
}

export interface BonusLogRow {
  at: string;
  item: BonusItemSnapshot;
}

export type SetItemResult =
  | "saved"
  | "scheduled"
  | "bad_item"
  | "bad_value"
  | "over_cap"
  | "bad_min_order"
  | "needs_non_alcoholic";
export type SetEnabledResult = "on" | "off" | "needs_item" | "needs_pin";
export type SetPinResult = "saved" | "bad_pin";

export interface BonusItemInput {
  name: string;
  nameRo: string;
  valueRon: number;
  minOrderRon: number | null;
  nonAlcoholic: boolean;
}

export interface OperatorBonusVenue extends VenueBonusConfig {
  name: string;
  city: string | null;
  visible: boolean;
  redemptions_today: number;
  wrong_pins_30d: number;
  flag_wrong_pins: boolean;
  flag_near_cap: boolean;
}

export interface OperatorBonusOverview {
  rules: Record<string, number | string | boolean | null>;
  totals: { active_bonuses: number; stamps_30d: number; redemptions_30d: number };
  venues: OperatorBonusVenue[];
}

async function call<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args);
  if (isMissingFunction(error)) throw new BonusUnavailable();
  if (error) throw error;
  return data as T;
}

// --- Consumer (§33c) ---------------------------------------------------------

/** The signed-in diner's Dine In tap. Raises like record_visit (too far, venue hidden). */
export const earnStamp = (venueId: string, coords: LatLng, accuracyM: number | null) =>
  call<EarnStampResult>("earn_stamp", {
    p_venue: venueId,
    p_lat: coords.lat,
    p_lng: coords.lng,
    p_accuracy: accuracyM == null ? null : Math.round(accuracyM),
    p_anon: getAnonId(),
  });

export const fetchMyBonus = () => call<MyBonus>("my_bonus");

export const fetchBonusVenues = () => call<BonusVenue[] | null>("bonus_venues").then((r) => r ?? []);

/** `sentAt` is this phone's clock when the request left: the redemption's
 *  countdown runs from there, so network latency can't make it outlast the
 *  server's `expires_at` (and a skewed phone clock can't shorten it). */
export async function startRedemption(
  venueId: string,
  coords: LatLng,
  accuracyM: number | null,
): Promise<StartRedemptionResult & { sentAt: number }> {
  const sentAt = Date.now();
  const res = await call<StartRedemptionResult>("start_redemption", {
    p_venue: venueId,
    p_lat: coords.lat,
    p_lng: coords.lng,
    p_accuracy: accuracyM == null ? null : Math.round(accuracyM),
  });
  return { ...res, sentAt };
}

export const confirmRedemption = (redemptionId: string, pin: string) =>
  call<ConfirmRedemptionResult>("confirm_redemption", { p_redemption: redemptionId, p_pin: pin });

// --- Venue side (§33d) -------------------------------------------------------

function itemArgs(i: BonusItemInput) {
  return {
    p_item: i.name,
    p_item_ro: i.nameRo.trim() ? i.nameRo : null,
    p_value: i.valueRon,
    p_min_order: i.minOrderRon,
    p_non_alcoholic: i.nonAlcoholic,
  };
}

export const fetchManagerBonusConfig = () => call<VenueBonusConfig>("manager_bonus_config");
export const managerSetBonusItem = (i: BonusItemInput) => call<SetItemResult>("manager_set_bonus_item", itemArgs(i));
export const managerSetBonusEnabled = (enabled: boolean) =>
  call<SetEnabledResult>("manager_set_bonus_enabled", { p_enabled: enabled });
export const managerSetBonusPin = (pin: string) => call<SetPinResult>("manager_set_bonus_pin", { p_pin: pin });
export const fetchManagerBonusLog = (limit = 50) =>
  call<BonusLogRow[] | null>("manager_bonus_log", { p_limit: limit }).then((r) => r ?? []);

export const fetchOperatorBonusOverview = () => call<OperatorBonusOverview>("operator_bonus_overview");
export const operatorSetBonusItem = (venueId: string, i: BonusItemInput) =>
  call<SetItemResult>("operator_set_bonus_item", { p_venue: venueId, ...itemArgs(i) });
export const operatorSetBonusEnabled = (venueId: string, enabled: boolean) =>
  call<SetEnabledResult>("operator_set_bonus_enabled", { p_venue: venueId, p_enabled: enabled });
export const operatorSetBonusPin = (venueId: string, pin: string) =>
  call<SetPinResult>("operator_set_bonus_pin", { p_venue: venueId, p_pin: pin });

/** The venue-side RPCs one editor needs: `/manage` binds the manager set, the
 *  operator editor binds the operator set to one venue. */
export interface BonusAdminApi {
  load: () => Promise<VenueBonusConfig>;
  setItem: (i: BonusItemInput) => Promise<SetItemResult>;
  setEnabled: (enabled: boolean) => Promise<SetEnabledResult>;
  setPin: (pin: string) => Promise<SetPinResult>;
  /** Only the manager side has the log RPC; the operator sees aggregates. */
  log?: () => Promise<BonusLogRow[]>;
}

export const managerBonusApi: BonusAdminApi = {
  load: fetchManagerBonusConfig,
  setItem: managerSetBonusItem,
  setEnabled: managerSetBonusEnabled,
  setPin: managerSetBonusPin,
  log: () => fetchManagerBonusLog(50),
};

export function operatorBonusApi(venueId: string): BonusAdminApi {
  return {
    // The operator has no per-venue read RPC: pick this venue out of the overview.
    load: async () => {
      const o = await fetchOperatorBonusOverview();
      const row = o.venues.find((v) => v.venue_id === venueId);
      return (
        row ?? {
          venue_id: venueId,
          enabled: false,
          disabled_at: null,
          item: null,
          next_item: null,
          next_effective_at: null,
          non_alcoholic_confirmed: false,
          pin_set: false,
          pin_set_at: null,
          max_item_value_ron: Number(o.rules.max_item_value_ron ?? 0),
          stamps_30d: 0,
          redemptions_30d: 0,
        }
      );
    },
    setItem: (i) => operatorSetBonusItem(venueId, i),
    setEnabled: (e) => operatorSetBonusEnabled(venueId, e),
    setPin: (p) => operatorSetBonusPin(venueId, p),
  };
}

// bonus_venues is public and changes rarely: one read per 5 minutes per tab.
const VENUES_TTL_MS = 5 * 60_000;
let venuesCache: { at: number; rows: Promise<BonusVenue[]>; settled: BonusVenue[] | null } | null = null;

/** Cached `bonus_venues()`; fail-soft to [] (missing RPC, network). */
export function loadBonusVenues(force = false): Promise<BonusVenue[]> {
  if (!force && venuesCache && Date.now() - venuesCache.at < VENUES_TTL_MS) return venuesCache.rows;
  const entry: NonNullable<typeof venuesCache> = { at: Date.now(), rows: Promise.resolve([]), settled: null };
  entry.rows = fetchBonusVenues()
    .then((r) => {
      entry.settled = r;
      return r;
    })
    .catch(() => {
      if (venuesCache === entry) venuesCache = null;
      return [] as BonusVenue[];
    });
  venuesCache = entry;
  return entry.rows;
}

/** The cached rows if a fresh read has already settled, else null. Lets the
 *  venue chip render on its first frame (no pop-in) once the feed warmed it. */
export function peekBonusVenues(): BonusVenue[] | null {
  if (!venuesCache || Date.now() - venuesCache.at >= VENUES_TTL_MS) return null;
  return venuesCache.settled;
}

// Venue coordinates never change in a session: read once per venue id.
const coordsCache = new Map<string, LatLng | null>();

/** Coordinates for the bonus venues (public columns), so the card can sort by
 *  distance and offer "Use bonus here" only within the check-in radius. Only
 *  ids not seen before are read; a failed read is retried on the next open. */
export async function fetchVenueCoords(ids: string[]): Promise<Record<string, LatLng>> {
  const missing = ids.filter((id) => !coordsCache.has(id));
  if (missing.length > 0) {
    const { data, error } = await supabase.from("restaurants").select("id, lat, lng").in("id", missing);
    if (!error && data) {
      for (const r of data as Array<{ id: string; lat: number | null; lng: number | null }>) {
        coordsCache.set(r.id, r.lat != null && r.lng != null ? { lat: r.lat, lng: r.lng } : null);
      }
    }
  }
  const out: Record<string, LatLng> = {};
  for (const id of ids) {
    const c = coordsCache.get(id);
    if (c) out[id] = c;
  }
  return out;
}
